// Redux Articles module
// see https://github.com/erikras/ducks-modular-redux

import { Article } from '../../models/';
import { Articles, Utils } from '../../services/';
import ArticlesApi from '../../services/rest/Articles';
import { createRestSlices, applyReducers } from './rest';
import { v4 } from 'uuid';
import * as _ from 'es-toolkit';
import { change, initialize } from 'redux-form';
import { name_for_new_variant } from '../../containers/Articles/articleFirstVariantName';
import {
    ARTICLE_FORM_NAME,
    get_article_form_initial_values,
    reset_article_form_initial_values_memo,
} from '../../containers/Articles/articleFormSession';

const { linkedResourcesReducer, linkedResourcesAction } = createRestSlices(ArticlesApi);

const RESET_ARTICLE                = 'kronos/articles/RESET_ARTICLE';
const LOADING_ARTICLES             = 'kronos/articles/LOADING_ARTICLES';
const LOADING_ARTICLES_SUCCESS     = 'kronos/articles/LOADING_ARTICLES_SUCCESS';
const LOADING_ARTICLES_FAILURE     = 'kronos/articles/LOADING_ARTICLES_FAILURE';
const LOADING_ARTICLE              = 'kronos/articles/LOADING_ARTICLE';
const LOADING_ARTICLE_SUCCESS      = 'kronos/articles/LOADING_ARTICLE_SUCCESS';
const LOADING_ARTICLE_FAILURE      = 'kronos/articles/LOADING_ARTICLE_FAILURE';
const UPDATING_ARTICLE             = 'kronos/articles/UPDATING_ARTICLE';
const UPDATING_ARTICLE_SUCCESS     = 'kronos/articles/UPDATING_ARTICLE_SUCCESS';
const UPDATING_ARTICLE_FAILURE     = 'kronos/articles/UPDATING_ARTICLE_FAILURE';
const START_CREATING_ARTICLE       = 'kronos/articles/START_CREATING_ARTICLE';
const CREATING_ARTICLE             = 'kronos/articles/CREATING_ARTICLE';
const CREATING_ARTICLE_SUCCESS     = 'kronos/articles/CREATING_ARTICLE_SUCCESS';
const CREATING_ARTICLE_FAILURE     = 'kronos/articles/CREATING_ARTICLE_FAILURE';
const DELETING_ARTICLE             = 'kronos/articles/DELETING_ARTICLE';
const DELETING_ARTICLE_SUCCESS     = 'kronos/articles/DELETING_ARTICLE_SUCCESS';
const DELETING_ARTICLE_FAILURE     = 'kronos/articles/DELETING_ARTICLE_FAILURE';
const DELETING_ARTICLE_RESET       = 'kronos/articles/DELETING_ARTICLE_RESET';
const ADD_ARTICLE_VARIANT          = 'kronos/articles/ADD_ARTICLE_VARIANT';
const REMOVE_ARTICLE_VARIANT       = 'kronos/articles/REMOVE_ARTICLE_VARIANT';
const DUPLICATE_ARTICLE_VARIANT    = 'kronos/articles/DUPLICATE_ARTICLE_VARIANT';
const ADD_ARTICLE_VARIANT_STOCK    = 'kronos/articles/ADD_ARTICLE_VARIANT_STOCK';
const ADD_ARTICLE_STOCK            = 'kronos/articles/ADD_ARTICLE_STOCK';
const REMOVE_ARTICLE_VARIANT_STOCK = 'kronos/articles/REMOVE_ARTICLE_VARIANT_STOCK';
const REMOVE_ARTICLE_STOCK         = 'kronos/articles/REMOVE_ARTICLE_STOCK';
const SET_CURRENT_TYPE             = 'kronos/articles/SET_CURRENT_TYPE';
const SET_CURRENT_CATEGORY         = 'kronos/articles/SET_CURRENT_CATEGORY';
const SET_SEARCH                   = 'kronos/articles/SET_SEARCH';
const ADD_ARTICLE_FEATURE          = 'kronos/articles/ADD_ARTICLE_FEATURE';
const REMOVE_ARTICLE_FEATURE       = 'kronos/articles/REMOVE_ARTICLE_FEATURE';
const ADD_ARTICLE_EXTRA            = 'kronos/articles/ADD_ARTICLE_EXTRA';
const REMOVE_ARTICLE_EXTRA         = 'kronos/articles/REMOVE_ARTICLE_EXTRA';
const ADD_ARTICLE_EXTRA_CHOICE     = 'kronos/articles/ADD_ARTICLE_EXTRA_CHOICE';
const REMOVE_ARTICLE_EXTRA_CHOICE  = 'kronos/articles/REMOVE_ARTICLE_EXTRA_CHOICE';

const initialState = {
    isLoading: false,
    hasLoadingError: false,
    loadingErrorMessage: '',
    articleSuccessfullyCreated: false,
    articles: [],
    search: null,
    currentCategory: null,
    currentType: Article.TYPE_PRODUCT,
    linkedResources: {},
}

const initialArticle = (type, _vat) => {
    return {
        "type": type,
        "name" : {},
        "short_description" : {},
        "description" : {},
        "category" : {},
        "pos" : [],
        "stock_type" : "none",
        "stocks" : [],
        "supplier" : "",
        "posters" : [],
        // Empty on create; first variant is seeded after save on the edit screen.
        "variants" : []
    };
};

const initialStock = {
    salepoint_ids: [],
    availability: 0
};

const initialVariant = () => {
    const v = {
        _id: v4(),
        name: {},
        price: {CHF: 0},
        value: {CHF: 0},
        purchasing_price: {CHF: 0},
        vat: 0,
        variable_price: false,
        value_like_price : true,
        gtin: "",
        sku: "",
        stock_factor: 0,
        stocks: [],
    };

    return v;
};

const initialDiscount = articleType => {
    const discount = {
        target : Article.DISCOUNT_TARGET_CART,
        value : {percentage : 0, amount : {}},
        rules : {only_for_roles: Utils.defaultPricingSellerRoles()}
    };
    if (articleType === Article.TYPE_CODE_DISCOUNT)
        discount.code = 'TKT' + parseInt(Math.random() * 999999, 10);
    if (articleType === Article.TYPE_AUTO_DISCOUNT) {
        discount.target = Article.DISCOUNT_TARGET_SKIP_FIRST;
        discount.skip = 1;
    }

    return discount;
}

const is_discount_article_type = type => ([
    Article.TYPE_MANUAL_DISCOUNT,
    Article.TYPE_AUTO_DISCOUNT,
    Article.TYPE_CODE_DISCOUNT,
].includes(type));

const create_initial_feature = () => ({
    name: {},
    max_nb_choices: 0,
    choices: [{
        name: {},
        price: { CHF: 0 }
    }]
});

const create_initial_extra = () => ({
    name: {},
    min_nb_choices: 0,
    choices: [{
        name: {},
        price: { CHF: 0 }
    }]
});

const create_initial_extra_choice = () => ({
    name: {},
    price: { CHF: 0 }
});

const plain_attributes = (attributes) => JSON.parse(JSON.stringify(attributes || []));

const overlay_form_attributes = (redux_attrs, form_attrs, patch = {}) => {
    let form = Array.isArray(form_attrs) ? form_attrs : [];
    if (patch.removed_attr_index != null)
        form = form.filter((_, i) => i !== patch.removed_attr_index);
    if (patch.removed_choice) {
        const { attributeIndex, choiceIndex } = patch.removed_choice;
        form = form.map((attr, i) => {
            if (i !== attributeIndex || !attr?.choices)
                return attr;
            return {
                ...attr,
                choices: attr.choices.filter((_, j) => j !== choiceIndex)
            };
        });
    }
    return plain_attributes(redux_attrs).map((attr, i) => {
        const from_form = form[i];
        if (!from_form)
            return attr;
        const choices = (attr.choices || []).map((choice, j) => {
            const form_choice = from_form.choices?.[j];
            return form_choice ? { ...choice, ...form_choice } : choice;
        });
        return { ...attr, ...from_form, choices };
    });
};

const sync_article_form_attributes = (dispatch, getState, patch) => {
    const redux_attrs = getState().articles?.article?.attributes || [];
    const form_attrs = getState().form?.[ARTICLE_FORM_NAME]?.values?.attributes;
    dispatch(change(
        ARTICLE_FORM_NAME,
        'attributes',
        overlay_form_attributes(redux_attrs, form_attrs, patch)
    ));
};

/** Keep dirty form fields for existing variants; inject newly added rows from the reducer. */
const overlay_form_variants = (redux_variants, form_variants) => {
    const by_id = new Map();
    (form_variants || []).forEach((variant) => {
        if (variant?._id)
            by_id.set(variant._id, variant);
    });
    return (redux_variants || []).map((variant) => {
        const from_form = by_id.get(variant._id);
        return from_form ? { ...variant, ...from_form, _id: variant._id } : variant;
    });
};

const sync_article_form_variants = (dispatch, getState) => {
    const redux_variants = getState().articles?.article?.variants || [];
    const form_variants = getState().form?.[ARTICLE_FORM_NAME]?.values?.variants;
    dispatch(change(
        ARTICLE_FORM_NAME,
        'variants',
        overlay_form_variants(redux_variants, form_variants)
    ));
};

const article_with_attributes = (article, attributes) => new Article({
    ...article,
    attributes
});

// Reducer
const reducer = (state = initialState, action) => {
    switch(action.type) {
        case RESET_ARTICLE:
            return {
                ...state,
                article: {},
                isLoading: false,
                hasLoadingError: false,
            };
        case LOADING_ARTICLES:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false,
            };
        case LOADING_ARTICLES_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                articles: action.articles,
            };
        case LOADING_ARTICLES_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case LOADING_ARTICLE:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case LOADING_ARTICLE_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                article: action.article,
                // Must stay false: this action used to run right after create
                // and cancelled the redirect to the edit form.
                articleSuccessfullyCreated: false,
            };
        case LOADING_ARTICLE_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case UPDATING_ARTICLE:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case UPDATING_ARTICLE_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                article: action.article
            };
        case UPDATING_ARTICLE_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                loadingErrorMessage: action.error.message
            };
        case START_CREATING_ARTICLE: {
            // Variants stay empty on create; discount defaults are applied when
            // the first variant is seeded on the edit screen (ADD_ARTICLE_VARIANT).
            const article = initialArticle(action.articleType || Article.TYPE_PRODUCT, action.vat);

            if (state.currentCategory?._id)
                article.category = { _id: state.currentCategory._id };

            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                articleSuccessfullyCreated: false,
                // Ensure sane default article for creation
                article
            };
        }

        case CREATING_ARTICLE:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case CREATING_ARTICLE_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                articleSuccessfullyCreated: true,
                article: action.article
            };
        case CREATING_ARTICLE_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                articleSuccessfullyCreated: false,
                loadingErrorMessage: action.error.message
            };
        case DELETING_ARTICLE:
            return {
                ...state,
                isLoading: true,
                hasLoadingError: false
            };
        case DELETING_ARTICLE_SUCCESS:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: false,
                articleSuccessfullyDeleted: true,
                article: action.article
            };
        case DELETING_ARTICLE_FAILURE:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                articleSuccessfullyDeleted: false,
                loadingErrorMessage: action.error.message
            };
        case DELETING_ARTICLE_RESET:
            return {
                ...state,
                isLoading: false,
                hasLoadingError: true,
                articleSuccessfullyDeleted: false,
                loadingErrorMessage: null
            };
        case ADD_ARTICLE_VARIANT_STOCK:
            state.article.variants[action.index].stocks.push(initialStock);
            return state;
        case ADD_ARTICLE_STOCK:
            state.article.stocks.push(initialStock);
            return state;
        case ADD_ARTICLE_VARIANT:
            const newVariant = {
                ...initialVariant(),
                vat: action.vat
            };


            const articleType = state.article?.type;
            const needsCode = articleType === Article.TYPE_CODE_DISCOUNT;
            const isDiscount = is_discount_article_type(articleType);
            const variants = state.article?.variants || [];
            const is_first = variants.length === 0;
            newVariant.name = name_for_new_variant({
                isFirst: is_first,
                langs: action.langs,
                articleName: state.article?.name,
                previousVariantName: variants[variants.length - 1]?.name,
                needsCode,
                generatedCode: v4(),
            });

            if (isDiscount)
                newVariant.discount = initialDiscount(articleType);

            return {
                ...state,
                article: {
                    ...state.article,
                    variants: [
                        ...state.article.variants,
                        newVariant
                    ]
                }
            }
        case REMOVE_ARTICLE_VARIANT:
            return {
                ...state,
                article: {
                    ...state.article,
                    variants: state.article.variants.filter(v => v._id !== action._id)
                }
            }
        case DUPLICATE_ARTICLE_VARIANT: {
            const original = state.article.variants.find(v => v._id === action._id);
            if (original) {
                const newVariant = _.cloneDeep(original);
                newVariant._id = v4();
                // Stock pool ids must be unique per article; cloning them breaks
                // Engine adjust (keyed historically by stock._id alone).
                if (Array.isArray(newVariant.stocks)) {
                    newVariant.stocks = newVariant.stocks.map(stock => ({
                        ...stock,
                        _id: v4(),
                    }));
                }
                return {
                    ...state,
                    article: {
                        ...state.article,
                        variants: [
                            ...state.article.variants,
                            newVariant
                        ]
                    }
                };
            }
            return state;
        }
        case REMOVE_ARTICLE_VARIANT_STOCK:
            state.article.variants[action.variant_index].stocks.splice(action.stock_index, 1);
            return state;
        case REMOVE_ARTICLE_STOCK:
            state.article.stocks.splice(action.index, 1);
            return state;
        case ADD_ARTICLE_FEATURE:
            return {
                ...state,
                article: article_with_attributes(state.article, [
                    ...(state.article.attributes || []),
                    create_initial_feature()
                ])
            };
        case REMOVE_ARTICLE_FEATURE:
            return {
                ...state,
                article: article_with_attributes(
                    state.article,
                    (state.article.attributes || []).filter((_, i) => i !== action.index)
                )
            };
        case ADD_ARTICLE_EXTRA:
            return {
                ...state,
                article: article_with_attributes(state.article, [
                    ...(state.article.attributes || []),
                    create_initial_extra()
                ])
            };
        case REMOVE_ARTICLE_EXTRA:
            return {
                ...state,
                article: article_with_attributes(
                    state.article,
                    (state.article.attributes || []).filter((_, i) => i !== action.index)
                )
            };
        case ADD_ARTICLE_EXTRA_CHOICE:
            return {
                ...state,
                article: article_with_attributes(
                    state.article,
                    (state.article.attributes || []).map((attr, i) => {
                        if (i !== action.index)
                            return attr;
                        return {
                            ...attr,
                            choices: [...(attr.choices || []), create_initial_extra_choice()]
                        };
                    })
                )
            };
        case REMOVE_ARTICLE_EXTRA_CHOICE:
            return {
                ...state,
                article: article_with_attributes(
                    state.article,
                    (state.article.attributes || []).map((attr, i) => {
                        if (i !== action.attributeIndex)
                            return attr;
                        return {
                            ...attr,
                            choices: (attr.choices || []).filter((_, j) => j !== action.choiceIndex)
                        };
                    })
                )
            };
        case SET_CURRENT_CATEGORY:
            return {
                ...state,
                currentCategory: action.currentCategory
            };
        case SET_CURRENT_TYPE:
            return {
                ...state,
                currentType: action.currentType
            };
        case SET_SEARCH:
            return {
                ...state,
                search: action.search
            };
        default:
            return state;
    }
};

export default (state = initialState, action) => {
    return applyReducers(state, action, [linkedResourcesReducer, reducer]);
};

// Actions
function loadingArticles() { return { type: LOADING_ARTICLES } }
function loadingArticlesSuccess(articles) { return { type: LOADING_ARTICLES_SUCCESS, articles: articles } }
function loadingArticlesFailure(err) { return { type: LOADING_ARTICLES_FAILURE, error: err } }
export function loadArticles(params) {
    return (dispatch) => {
        dispatch(loadingArticles());
        Articles.list(params)
            .then(data => {
                const articles = data.articles;
                dispatch(loadingArticlesSuccess(articles));
            })
            .catch(err => {
                console.log("err => ", err);
                dispatch(loadingArticlesFailure(err))
            });
    }
}

export function resetArticle() { return { type: RESET_ARTICLE } }
function loadingArticle() { return { type: LOADING_ARTICLE } }
function loadingArticleSuccess(article) { return { type: LOADING_ARTICLE_SUCCESS, article: article } }
function loadingArticleFailure(err) { return { type: LOADING_ARTICLE_FAILURE, error: err } }
export function loadArticle(articleId) {
    return (dispatch) => {
        dispatch(loadingArticle());
        Articles.get(articleId)
            .then(data => {
                const article = data.article;
                dispatch(loadingArticleSuccess(article));
            })
            .catch(err => {
                dispatch(loadingArticleFailure(err))
            });
    }
}

function updatingArticle() { return { type: UPDATING_ARTICLE } }
function updatingArticleSuccess(article) { return { type: UPDATING_ARTICLE_SUCCESS, article: article } }
function updatingArticleFailure(err) { return { type: UPDATING_ARTICLE_FAILURE, error: err } }
export function updateArticle(article) {
    return (dispatch) => {
        dispatch(updatingArticle());
        return Articles.update(article)
            .then(data => {
                const updated = data.article;
                dispatch(updatingArticleSuccess(updated));
                // keepDirtyOnReinitialize would otherwise keep a stale dirty
                // `new_poster` and re-append the same image on every save.
                reset_article_form_initial_values_memo();
                dispatch(initialize(
                    ARTICLE_FORM_NAME,
                    get_article_form_initial_values(updated),
                    false
                ));
                return updated;
            })
            .catch(err => {
                dispatch(updatingArticleFailure(err));
                return null;
            });
    }
}

function creatingArticle() { return { type: CREATING_ARTICLE } }
function creatingArticleSuccess(article) { return { type: CREATING_ARTICLE_SUCCESS, article: article } }
function creatingArticleFailure(err) { return { type: CREATING_ARTICLE_FAILURE, error: err } }
export function startCreateArticle(articleType, vat) { return { type: START_CREATING_ARTICLE, articleType, vat } }
export function createArticle(article) {
    return (dispatch, getState) => {
        const { isLoading, articleSuccessfullyCreated } = getState().articles;
        // A second click (Safari) or LOADING_ARTICLE_SUCCESS used to clear the
        // create-success flag and POST /article/create again with the new _id.
        if (isLoading || articleSuccessfullyCreated)
            return;

        dispatch(creatingArticle());
        return Articles.create(article)
            .then(data => {
                dispatch(creatingArticleSuccess(data.article));
            })
            .catch(err => {
                dispatch(creatingArticleFailure(err))
            });
    }
}

function deletingArticle() { return { type: DELETING_ARTICLE } }
function deletingArticleSuccess() { return { type: DELETING_ARTICLE_SUCCESS } }
function deletingArticleFailure(err) { return { type: DELETING_ARTICLE_FAILURE, error: err } }
export function deleteArticle(articleId) {
    return (dispatch) => {
        dispatch(deletingArticle());
        Articles.delete(articleId)
            .then(() => {
                dispatch(deletingArticleSuccess());
            })
            .catch(err => {
                dispatch(deletingArticleFailure(err))
            });
    }
}
export function resetDeleteArticle() {
    return dispatch => {
        dispatch({ type: DELETING_ARTICLE_RESET });
    };
};

export function addArticleVariantStock(i) { return { type: ADD_ARTICLE_VARIANT_STOCK, index: i } }
export function addArticleStock() { return { type: ADD_ARTICLE_STOCK } }
export function addArticleVariant() {
    const vat   = Utils.tax()?.vat_articles ?? 0;
    const langs = Utils.langs();
    return (dispatch, getState) => {
        dispatch({ type: ADD_ARTICLE_VARIANT, vat, langs });
        // keepDirtyOnReinitialize does not inject new array rows into the form.
        sync_article_form_variants(dispatch, getState);
    };
}
export function removeArticleVariant(_id) {
    return (dispatch, getState) => {
        dispatch({ type: REMOVE_ARTICLE_VARIANT, _id });
        sync_article_form_variants(dispatch, getState);
    };
}
export function duplicateArticleVariant(_id) {
    return (dispatch, getState) => {
        dispatch({ type: DUPLICATE_ARTICLE_VARIANT, _id });
        sync_article_form_variants(dispatch, getState);
    };
}
export function removeArticleVariantStock(vi, si) { return { type: REMOVE_ARTICLE_VARIANT_STOCK, variant_index: vi, stock_index: si} }
export function removeArticleStock(i) { return { type: REMOVE_ARTICLE_STOCK, index: i} }
export function setCurrentCategory(currentCategory) { return { type: SET_CURRENT_CATEGORY, currentCategory } }
export function setCurrentType(currentType) { return { type: SET_CURRENT_TYPE, currentType } }
export function setSearch(search) { return { type: SET_SEARCH, search } }
export function addArticleFeature() {
    return (dispatch, getState) => {
        dispatch({ type: ADD_ARTICLE_FEATURE });
        sync_article_form_attributes(dispatch, getState);
    };
}
export function removeArticleFeature(i) {
    return (dispatch, getState) => {
        dispatch({ type: REMOVE_ARTICLE_FEATURE, index: i });
        sync_article_form_attributes(dispatch, getState, { removed_attr_index: i });
    };
}
export function addArticleExtra() {
    return (dispatch, getState) => {
        dispatch({ type: ADD_ARTICLE_EXTRA });
        sync_article_form_attributes(dispatch, getState);
    };
}
export function removeArticleExtra(i) {
    return (dispatch, getState) => {
        dispatch({ type: REMOVE_ARTICLE_EXTRA, index: i });
        sync_article_form_attributes(dispatch, getState, { removed_attr_index: i });
    };
}
export function addArticleExtraChoice(index) {
    return (dispatch, getState) => {
        dispatch({ type: ADD_ARTICLE_EXTRA_CHOICE, index });
        sync_article_form_attributes(dispatch, getState);
    };
}
export function removeArticleExtraChoice(attributeIndex, choiceIndex) {
    return (dispatch, getState) => {
        dispatch({ type: REMOVE_ARTICLE_EXTRA_CHOICE, attributeIndex, choiceIndex });
        sync_article_form_attributes(dispatch, getState, {
            removed_choice: { attributeIndex, choiceIndex }
        });
    };
}

export const getArticlesLinkedResources = linkedResourcesAction;
